<template>
	<v-card dark outlined class="pa-3" style="background:#1E1E1E;">

		<!-- Title row -->
		<div class="d-flex align-center mb-2">
			<v-icon small left color="cyan lighten-1">mdi-chart-line</v-icon>
			<span class="caption font-weight-bold text-uppercase grey--text text--lighten-1">
				{{ $ml.get('alarm_stats_throughput') }}
			</span>
		</div>

		<!-- Time interval preset buttons -->
		<div class="d-flex align-center flex-wrap mb-2" style="gap:4px;">
			<v-btn-toggle
				v-model="selectedPreset"
				dense
				borderless
				background-color="transparent"
				class="mr-1"
			>
				<v-btn
					v-for="preset in presetOptions"
					:key="preset.value"
					:value="preset.value"
					x-small
					outlined
					:color="selectedPreset === preset.value ? 'cyan lighten-1' : 'grey darken-1'"
					class="caption"
					style="min-width:36px;"
					@click="setPreset(preset.value)"
				>{{ preset.label }}</v-btn>
			</v-btn-toggle>

			<!-- All / Reset button -->
			<v-btn
				x-small
				outlined
				:color="selectedPreset === null ? 'cyan lighten-1' : 'grey darken-1'"
				class="caption"
				style="min-width:36px;"
				@click="clearFilter"
			>{{ $ml.get('alarm_stats_all') }}</v-btn>

			<!-- Reset zoom button — only visible when chart is zoomed -->
			<v-btn
				v-if="isZoomed"
				x-small
				outlined
				color="orange lighten-1"
				class="caption ml-1"
				@click="resetZoom"
			>
				<v-icon x-small left>mdi-magnify-minus</v-icon>
				{{ $ml.get('alarm_stats_reset_zoom') }}
			</v-btn>
		</div>

		<!-- Custom range inputs -->
		<div v-if="showCustomRange" class="d-flex align-center flex-wrap mb-2" style="gap:8px;">
			<!-- From date -->
			<v-menu
				v-model="menuFrom"
				:close-on-content-click="false"
				transition="scale-transition"
				offset-y
				min-width="290px"
			>
				<template v-slot:activator="{ on }">
					<v-text-field
						v-model="customDateFrom"
						:label="$ml.get('alarm_stats_from')"
						prepend-inner-icon="mdi-calendar"
						readonly
						dense
						outlined
						dark
						hide-details
						style="max-width:150px; background:#2D2D2D;"
						v-on="on"
					></v-text-field>
				</template>
				<v-date-picker
					v-model="customDateFrom"
					no-title
					dark
					color="cyan lighten-1"
					@input="menuFrom = false"
				></v-date-picker>
			</v-menu>

			<!-- From time -->
			<v-text-field
				v-model="customTimeFrom"
				type="time"
				:label="$ml.get('alarm_stats_time')"
				dense
				outlined
				dark
				hide-details
				style="max-width:110px; background:#2D2D2D;"
			></v-text-field>

			<span class="caption grey--text mx-1">—</span>

			<!-- To date -->
			<v-menu
				v-model="menuTo"
				:close-on-content-click="false"
				transition="scale-transition"
				offset-y
				min-width="290px"
			>
				<template v-slot:activator="{ on }">
					<v-text-field
						v-model="customDateTo"
						:label="$ml.get('alarm_stats_to')"
						prepend-inner-icon="mdi-calendar"
						readonly
						dense
						outlined
						dark
						hide-details
						style="max-width:150px; background:#2D2D2D;"
						v-on="on"
					></v-text-field>
				</template>
				<v-date-picker
					v-model="customDateTo"
					no-title
					dark
					color="cyan lighten-1"
					@input="menuTo = false"
				></v-date-picker>
			</v-menu>

			<!-- To time -->
			<v-text-field
				v-model="customTimeTo"
				type="time"
				:label="$ml.get('alarm_stats_time')"
				dense
				outlined
				dark
				hide-details
				style="max-width:110px; background:#2D2D2D;"
			></v-text-field>

			<!-- Apply button -->
			<v-btn
				small
				color="cyan darken-1"
				dark
				@click="applyCustomRange"
			>{{ $ml.get('alarm_stats_apply') }}</v-btn>
		</div>

		<!-- Canvas wrapper -->
		<div class="chart-wrapper" style="position:relative; height:200px;">
			<canvas ref="throughputCanvas"></canvas>
			<div
				v-if="!throughputData || throughputData.length === 0"
				class="d-flex align-center justify-center"
				style="position:absolute;top:0;left:0;right:0;bottom:0;"
			>
				<span class="grey--text caption">{{ $ml.get('no_data') }}</span>
			</div>
		</div>

		<!-- Legend -->
		<div class="d-flex align-center justify-center mt-2" style="gap:16px;">
			<div class="d-flex align-center">
				<span
					style="display:inline-block;width:12px;height:3px;background:#FFA726;margin-right:6px;border-radius:2px;"
				></span>
				<span class="caption grey--text text--lighten-1">{{ $ml.get('alarm_stats_rate') }}</span>
			</div>
			<div class="d-flex align-center">
				<span
					style="display:inline-block;width:12px;height:3px;background:#EF5350;margin-right:6px;border-radius:2px;"
				></span>
				<span class="caption grey--text text--lighten-1">{{ $ml.get('alarm_stats_defects_label') }}</span>
			</div>
			<div class="d-flex align-center">
				<span
					style="display:inline-block;width:12px;height:8px;background:#26C6DA33;border:1px solid #26C6DA;margin-right:6px;border-radius:2px;"
				></span>
				<span class="caption grey--text text--lighten-1">{{ $ml.get('alarm_stats_stoppages_label') }}</span>
			</div>
		</div>

	</v-card>
</template>

<script>
import Chart from 'chart.js';
import 'chartjs-plugin-zoom';

/**
 * Chart.js 2 plugin: draws cyan background bands for stoppage ranges
 * behind the data lines but in front of the grid.
 * Ranges are stored on chart.config._stoppageRanges as [{ startIndex, endIndex }].
 */
const stoppageBandPlugin = {
	beforeDatasetsDraw(chart) {
		const ranges = chart.config._stoppageRanges;
		if (!ranges || ranges.length === 0) return;

		const ctx   = chart.chart.ctx;           // Chart.js 2: chart.chart.ctx
		const xAxis = chart.scales['x-axis-0'];
		const yAxis = chart.scales['y-axis-0'];

		if (!xAxis || !yAxis) return;

		const labelCount  = chart.data.labels.length;
		if (labelCount === 0) return;

		// Half bar-slot width so the band covers the full bucket (not just the centre point)
		const slotWidth = xAxis.width / labelCount;
		const halfSlot  = slotWidth / 2;

		ctx.save();

		for (const range of ranges) {
			const x1 = xAxis.getPixelForValue(null, range.startIndex)  - halfSlot;
			const x2 = xAxis.getPixelForValue(null, range.endIndex)    + halfSlot;
			const w  = x2 - x1;
			const h  = yAxis.bottom - yAxis.top;

			// Fill band
			ctx.fillStyle = 'rgba(38, 198, 218, 0.10)';
			ctx.fillRect(x1, yAxis.top, w, h);

			// Left/right border lines for visual clarity
			ctx.strokeStyle = 'rgba(38, 198, 218, 0.40)';
			ctx.lineWidth   = 1;
			ctx.beginPath();
			ctx.moveTo(x1, yAxis.top);
			ctx.lineTo(x1, yAxis.bottom);
			ctx.moveTo(x2, yAxis.top);
			ctx.lineTo(x2, yAxis.bottom);
			ctx.stroke();
		}

		ctx.restore();
	},
};

export default {
	name : 'StatsThroughputChart',

	props : {
		throughputData : {
			type    : Array,
			default : () => [],
		},
	},

	data() {
		return {
			chart           : null,
			isZoomed        : false,

			// Preset selection: 'today' | '24h' | '12h' | '6h' | '1h' | 'custom' | null
			selectedPreset  : null,

			// Active filter timestamps (ms)
			activeFrom      : null,
			activeTo        : null,

			// Custom range UI state
			showCustomRange : false,
			customDateFrom  : null,
			customDateTo    : null,
			customTimeFrom  : '00:00',
			customTimeTo    : '23:59',
			menuFrom        : false,
			menuTo          : false,
		};
	},

	computed : {
		// Preset buttons — computed so labels react to language changes
		presetOptions() {
			return [
				{ value: 'today',  label: this.$ml.get('alarm_stats_preset_today')  },
				{ value: '24h',    label: '24h' },
				{ value: '12h',    label: '12h' },
				{ value: '6h',     label: '6h'  },
				{ value: '1h',     label: '1h'  },
				{ value: 'custom', label: this.$ml.get('alarm_stats_preset_custom') },
			];
		},

		/**
		 * Client-side filtered slice of throughputData based on active time bounds.
		 * When no filter is active, returns the full array.
		 */
		filteredData() {
			if (!this.throughputData || this.throughputData.length === 0) return [];
			if (!this.activeFrom && !this.activeTo) return this.throughputData;

			return this.throughputData.filter(d => {
				const t = new Date(d.bucket_time).getTime();
				if (this.activeFrom && t < this.activeFrom) return false;
				if (this.activeTo   && t > this.activeTo)   return false;
				return true;
			});
		},
	},

	watch : {
		/**
		 * React to filteredData changes (covers both new incoming data AND preset changes).
		 * We skip redundant updates by checking length + last bucket identity.
		 */
		filteredData(newVal, oldVal) {
			if (!this.chart) return;
			if (
				oldVal
				&& newVal
				&& oldVal.length === newVal.length
				&& oldVal.length > 0
				&& oldVal[oldVal.length - 1].bucket_time === newVal[newVal.length - 1].bucket_time
				&& oldVal[oldVal.length - 1].count === newVal[newVal.length - 1].count
			) {
				return;
			}
			this.updateChart(newVal);
		},
	},

	mounted() {
		// Default: last 60 min (live sliding window). Far more readable than the
		// whole day (hundreds of minute-buckets) — live changes and defects are
		// clearly visible. User can still switch to today/12h/etc. with the buttons.
		this.setPreset('1h');
		this.$nextTick(() => {
			this.createChart();
		});
	},

	beforeDestroy() {
		if (this.chart) {
			this.chart.destroy();
			this.chart = null;
		}
	},

	methods : {
		// ─── Preset / Filter methods ──────────────────────────────────────────

		setPreset(preset) {
			// v-btn-toggle already sets selectedPreset via v-model,
			// but we call this explicitly for logic side-effects.
			this.selectedPreset = preset;

			if (preset === 'custom') {
				this.showCustomRange = true;
				// Don't change activeFrom/activeTo yet — user must click Apply
				return;
			}

			this.showCustomRange = false;

			if (preset === 'today') {
				const tz = this.$timezone || this.$momentz.tz.guess();
				this.activeFrom = this.$momentz.tz(tz).startOf('day').valueOf();
				this.activeTo   = null;
			} else {
				const hours = { '24h': 24, '12h': 12, '6h': 6, '1h': 1 };
				const now = Date.now();
				this.activeFrom = now - (hours[preset] * 60 * 60 * 1000);
				this.activeTo   = null; // no upper bound = up to now
			}

			this.$emit('timeframe-change', {
				preset,
				from : new Date(this.activeFrom).toISOString(),
				to   : null,
			});
		},

		clearFilter() {
			this.selectedPreset  = null;
			this.activeFrom      = null;
			this.activeTo        = null;
			this.showCustomRange = false;

			this.$emit('timeframe-change', { preset: null, from: null, to: null });
		},

		applyCustomRange() {
			if (!this.customDateFrom || !this.customDateTo) return;

			const fromStr = `${this.customDateFrom}T${this.customTimeFrom || '00:00'}:00`;
			const toStr   = `${this.customDateTo}T${this.customTimeTo   || '23:59'}:00`;

			const fromMs = new Date(fromStr).getTime();
			const toMs   = new Date(toStr).getTime();

			if (isNaN(fromMs) || isNaN(toMs)) return;

			this.activeFrom = fromMs;
			this.activeTo   = toMs;

			this.$emit('timeframe-change', {
				preset : 'custom',
				from   : new Date(fromMs).toISOString(),
				to     : new Date(toMs).toISOString(),
			});
		},

		resetZoom() {
			if (this.chart) {
				this.chart.resetZoom();
				this.isZoomed = false;
			}
		},

		// ─── Chart lifecycle ─────────────────────────────────────────────────

		/**
		 * Merge consecutive idle buckets into index ranges for stoppage bands.
		 * A bucket counts as "stopped" only if had_stoppage=true AND count=0.
		 * Buckets with production (count > 0) are never shown as stopped,
		 * even if a stoppage occurred during that minute.
		 */
		mergeStoppageRanges(buckets) {
			const ranges = [];
			let current  = null;

			for (let i = 0; i < buckets.length; i++) {
				const b = buckets[i];
				if (b.had_stoppage && (!b.count || b.count === 0)) {
					if (!current) {
						current = { startIndex: i, endIndex: i };
					} else {
						current.endIndex = i;
					}
				} else {
					if (current) {
						ranges.push(current);
						current = null;
					}
				}
			}

			if (current) ranges.push(current);
			return ranges;
		},

		/**
		 * Build labels + datasets from data array.
		 * Stoppages are handled by the stoppageBandPlugin — no bar dataset here.
		 */

		/**
		 * Adaptive visual params based on data density.
		 * Many points → smaller/hidden points, thinner lines;
		 * few points  → larger points, thicker lines.
		 */
		getAdaptiveStyle(count) {
			if (count > 600) {
				// 10h+ of minute data — very dense
				return { pointRadius: 0, pointHoverRadius: 3, borderWidth: 1.5, fill: true };
			}
			if (count > 200) {
				// 3-10h — moderate density
				return { pointRadius: 0, pointHoverRadius: 4, borderWidth: 1.5, fill: true };
			}
			if (count > 60) {
				// 1-3h — comfortable
				return { pointRadius: 2, pointHoverRadius: 4, borderWidth: 2, fill: false };
			}
			// < 60 points — spacious
			return { pointRadius: 3, pointHoverRadius: 5, borderWidth: 2, fill: false };
		},

		buildChartData(data) {
			if (!data || data.length === 0) {
				return {
					labels   : [],
					datasets : [],
				};
			}

			const labels     = data.map(d => this.formatBucketTime(d.bucket_time));
			const rateData   = data.map(d => d.count        != null ? d.count        : 0);
			const defectData = data.map(d => d.defect_count != null ? d.defect_count : 0);

			const style = this.getAdaptiveStyle(data.length);

			return {
				labels,
				datasets : [
					// Rate line — dataset[0] (filled area for a clear production trend)
					{
						type             : 'line',
						label            : this.$ml.get('alarm_stats_rate'),
						data             : rateData,
						borderColor      : '#FFA726',
						backgroundColor  : 'rgba(255,167,38,0.18)',
						borderWidth      : Math.max(2, style.borderWidth),
						pointRadius      : style.pointRadius,
						pointHoverRadius : Math.max(5, style.pointHoverRadius),
						pointBackgroundColor : '#FFA726',
						fill             : true,
						lineTension      : 0.25,
						order            : 2,
						yAxisID          : 'y-axis-0',
					},
					// Defects — dataset[1] as BARS on their own right-side axis, so each
					// minute's defects stand out clearly even when production is high.
					{
						type             : 'bar',
						label            : this.$ml.get('alarm_stats_defects_label'),
						data             : defectData,
						backgroundColor  : 'rgba(239,83,80,0.85)',
						borderColor      : '#EF5350',
						borderWidth      : 0,
						hoverBackgroundColor : '#FF6E6E',
						categoryPercentage   : 0.9,
						barPercentage        : 0.9,
						yAxisID          : 'y-axis-defects',
					},
				],
			};
		},

		createChart() {
			if (!this.$refs.throughputCanvas) return;

			const ctx       = this.$refs.throughputCanvas.getContext('2d');
			const data      = this.filteredData;
			const chartData = this.buildChartData(data);
			const ranges    = this.mergeStoppageRanges(data);

			const self = this;

			const config = {
				type    : 'line',
				data    : chartData,
				plugins : [ stoppageBandPlugin ],
				options : {
					responsive          : true,
					maintainAspectRatio : false,
					animation           : { duration: 300 },

					// Force linear interpolation globally (Chart.js 2)
					elements : {
						line : { tension: 0 },
					},

					legend : {
						display : false, // use custom legend in template
					},

					tooltips : {
						mode            : 'index',
						intersect       : false,
						backgroundColor : '#2D2D2D',
						titleFontColor  : '#ffffff',
						bodyFontColor   : '#cccccc',
						borderColor     : '#444',
						borderWidth     : 1,
					},

					scales : {
						xAxes : [{
							id        : 'x-axis-0',
							display   : true,
							gridLines : { color: '#333333', zeroLineColor: '#444' },
							ticks     : {
								fontColor     : '#aaaaaa',
								maxRotation   : 45,
								autoSkip      : true,
								maxTicksLimit : 30,
							},
						}],
						yAxes : [{
							id         : 'y-axis-0',
							display    : true,
							position   : 'left',
							gridLines  : { color: '#333333', zeroLineColor: '#555' },
							ticks      : {
								fontColor   : '#aaaaaa',
								beginAtZero : true,
							},
							scaleLabel : {
								display     : true,
								labelString : this.$ml.get('alarm_stats_chart_units_axis'),
								fontColor   : '#888888',
							},
						}, {
							// Secondary axis for defects — scaled independently so a few
							// defects are visible even when the rate line is in the hundreds.
							id         : 'y-axis-defects',
							display    : true,
							position   : 'right',
							gridLines  : { drawOnChartArea: false, color: '#3a2a2a' },
							ticks      : {
								fontColor   : '#EF5350',
								beginAtZero : true,
								precision   : 0,
								suggestedMax: 5,
							},
							scaleLabel : {
								display     : true,
								labelString : this.$ml.get('alarm_stats_defects_label') || 'Defects',
								fontColor   : '#EF5350',
							},
						}],
					},

					// chartjs-plugin-zoom v0.7.x config (must be under options.plugins.zoom)
					plugins : {
						zoom : {
							zoom : {
								enabled     : true,
								drag        : false,
								mode        : 'x',
								speed       :  0.5,
								sensitivity :  1,
								onZoom      : function() { self.isZoomed = true; },
							},
							pan : {
								enabled   : true,
								mode      : 'x',
								speed     : 20,
								threshold : 10,
								onPan     : function() { self.isZoomed = true; },
							},
						},
					},
				},
			};

			// Store merged stoppage ranges so the plugin can read them
			config._stoppageRanges = ranges;

			this.chart = new Chart(ctx, config);
		},

		updateChart(data) {
			if (!this.chart) return;

			const chartData = this.buildChartData(data);
			const style     = this.getAdaptiveStyle((data || []).length);

			// Mutate labels in-place (don't replace array reference)
			this.chart.data.labels.length = 0;
			chartData.labels.forEach(l => this.chart.data.labels.push(l));

			// Mutate dataset data in-place + update adaptive visual style
			chartData.datasets.forEach((ds, i) => {
				if (this.chart.data.datasets[i]) {
					const existing = this.chart.data.datasets[i];
					existing.data.length = 0;
					ds.data.forEach(d => existing.data.push(d));

					// Adaptive density styling applies ONLY to the rate LINE; the
					// defects BAR keeps its own styling (don't clobber it).
					if (existing.type !== 'bar') {
						existing.borderWidth      = Math.max(2, style.borderWidth);
						existing.pointRadius      = style.pointRadius;
						existing.pointHoverRadius = Math.max(5, style.pointHoverRadius);
						existing.fill             = true; // always a filled area
					}
				}
			});

			// Update stoppage bands from the filtered data
			this.chart.config._stoppageRanges = this.mergeStoppageRanges(data || []);

			// Short animation → live changes are clearly perceived (the 1h window is
			// light enough that animating doesn't flicker like the full-day view did).
			this.chart.update({ duration: 250 });
		},

		/**
		 * Format a bucket_time string or ISO timestamp to HH:MM.
		 * bucket_time may be ISO ("2026-04-11T14:30:00") or plain time string.
		 */
		formatBucketTime(bucketTime) {
			if (!bucketTime) return '';
			try {
				const m = this.$momentz.tz(bucketTime, this.$timezone);
				return m.isValid() ? m.format('HH:mm') : String(bucketTime);
			} catch (e) {
				return String(bucketTime);
			}
		},

		/**
		 * Public method: append a single data point in real-time without full re-render.
		 * Expects: { bucket_time, count, defect_count, had_stoppage }
		 *
		 * Note: appends to the live chart directly. If a time filter is active,
		 * the point is only appended visually if it falls within the active range.
		 */
		appendPoint(point) {
			if (!this.chart || !point) return;

			// If a time filter is active, check whether the new point falls in range
			if (this.activeFrom || this.activeTo) {
				const t = new Date(point.bucket_time).getTime();
				if (this.activeFrom && t < this.activeFrom) return;
				if (this.activeTo   && t > this.activeTo)   return;
			}

			const label = this.formatBucketTime(point.bucket_time);

			this.chart.data.labels.push(label);
			// dataset[0] = rate, [1] = defects  (stoppage bar removed)
			this.chart.data.datasets[0].data.push(point.count        != null ? point.count        : 0);
			this.chart.data.datasets[1].data.push(point.defect_count != null ? point.defect_count : 0);

			// Rebuild stoppage ranges from the filtered data (which includes the new point
			// via the throughputData prop — parent adds the point to the array before calling appendPoint)
			this.chart.config._stoppageRanges = this.mergeStoppageRanges(this.filteredData);

			this.chart.update();
		},
	},
};
</script>
